// Module 02 — topology: Zod schemas for the SOURCE shape of data/reduced/*.json. These validate
// the committed data exactly as it sits on disk — including the richer source vocabulary the loader
// later normalizes into the frozen domain enums (vTier "UNK"; edge kind "corridor"; fuel synonyms;
// GeoJSON areaPolygon; null name/operator). Validation lives in topology (CLAUDE.md rule on deps).
import { z } from "zod";

const Coord = z.object({ lat: z.number(), lon: z.number() });

// GeoJSON position is [lon, lat, ...]; ring is a closed list of positions.
const Position = z.array(z.number());
const Ring = z.array(Position);
const GeoPolygon = z.object({ type: z.literal("Polygon"), coordinates: z.array(Ring) });
const GeoMultiPolygon = z.object({
  type: z.literal("MultiPolygon"),
  coordinates: z.array(z.array(Ring)),
});
export const GeoArea = z.discriminatedUnion("type", [GeoPolygon, GeoMultiPolygon]);

export const SourceNode = z.object({
  id: z.string(),
  cls: z.enum([
    "generator",
    "substation",
    "switch",
    "transformer",
    "junction",
    "consumer",
    "prosumer",
  ]),
  name: z.string().nullish(), // source uses null for unnamed nodes
  coord: Coord,
  vTier: z.enum(["EHV", "HV", "ST", "MV", "LV", "UNK"]), // "UNK" = unknown-voltage junction
  nominalKv: z.number(),
  busType: z.enum(["PQ", "PV", "slack"]),
  isTransformerStation: z.boolean().optional(),
  operator: z.string().nullish(),
  degree: z.number(),
  // settlement-chain ownership (build log 47) — derived in reduce.py; optional for older data.
  ownerRole: z.enum(["TRANSMITTER", "LDC", "GENERATOR", "CONSUMER", "JUNCTION"]).optional(),
  ownerName: z.string().nullish(),
  // validated transformer attributes (build log 59) — optional for older data.
  transformerStationMva: z.number().nullish(),
  transformation: z.string().nullish(),
  transformerZPct: z.number().nullish(),
  // ONT full-integration: a transmission station kept for DISPLAY but excluded from the solve/outages
  // (no electrical path to generation after bridging — visible infrastructure, not a blackout).
  displayOnly: z.boolean().optional(),
  // V4 §3.5(c) (additive): the OSM id of this facility's footprint parcel polygon (the SEPARATE pickable
  // parcels layer joins parcel→node by this id), so a node↔parcel selection round-trips both ways.
  parcelId: z.string().nullish(),
  // V3-swap (build log 75, additive): the OSM hub id a CORE node merged from (was silently stripped pre-
  // .strict()), plus superior V3 facility attrs (connection kV, station-MVA basis, provenance labels).
  osmHub: z.string().nullish(),
  connectionKv: z.number().nullish(),
  stationMvaBasis: z.string().nullish(),
  attrSource: z.string().nullish(),
  attrConfidence: z.string().nullish(),
}).strict(); // MF3 fail-loud: an unschematized field now THROWS at load instead of silently dropping (no-downgrade guard)

export const SourceEdge = z.object({
  id: z.string(),
  u: z.string(),
  v: z.string(),
  kind: z.enum([
    "line",
    "distribution_feeder",
    "neighbour_tie",
    "gen_interconnect",
    "merged",
    "corridor", // source-only; normalized to "line"
  ]),
  vTier: z.enum(["EHV", "HV", "ST", "MV", "LV"]),
  kv: z.number(),
  rPu: z.number(),
  xPu: z.number(),
  bPu: z.number(),
  rateMva: z.number(),
  lengthKm: z.number(),
  circuits: z.number(),
  polyline: z.array(Coord).min(2),
  origBranchIds: z.array(z.string()),
  isTie: z.boolean().optional(),
  isSwitchable: z.boolean().optional(),
  // validated line attributes (build log 59) — optional for older data.
  conductor: z.string().nullish(),
  thermalMva: z.number().nullish(),
  voltageInferred: z.boolean().optional(),
  // ONT full-integration: display-only corridor (touches a display-only station) / an inferred bridge
  // that closed an OSM mapping gap to electrically connect a disconnected transmission component.
  displayOnly: z.boolean().optional(),
  // V4 P-FIX (2026-06-28): this edge is a TRANSFORMER winding (conform maps origin kind 'transformer'→'line' so
  // the DC engine treats it as a plain xPu branch). The renderer reads this to draw it SOLID, never as a marching
  // arrow — a ~0-length winding carrying an animated chevron reads as a wrong-direction arrow on the backbone.
  isTransformerLeg: z.boolean().optional(),
  inferredBridge: z.boolean().optional(),
  // Core-A parallel-circuit identity: siblings of one corridor share corridorId + carry a 1-based index.
  corridorId: z.string().optional(),
  circuitIndex: z.number().optional(),
  // Phase-0: corridor impedance heterogeneity = max(xPu)/min(xPu) over siblings (1.0 = identical).
  maxXpuRatioSiblings: z.number().optional(),
  // Phase-0: sibling shares its route with another (coincident ⇒ fan apart); false ⇒ distinct route, no fan.
  coincident: z.boolean().optional(),
  // Core-B: a load's former feed retained as a normally-open FLISR backup after re-homing to the backbone.
  normallyOpen: z.boolean().optional(),
  // P1 reality fix: honest provenance for OSM-unmapped-but-REAL connectors (supersede the blanket
  // `inferredBridge`). serviceConnection = a distribution TS feeding its co-located neighbourhood (real
  // service drop); undergroundInferred = a real (downtown urban) cable interconnect OSM never mapped;
  // spurConnector = a real station's second tie that OSM left one-ended; realCorridor = a research-confirmed
  // named corridor (carries corridorRef/Source/Confidence). All carry realistic per-voltage-class impedance.
  serviceConnection: z.boolean().optional(),
  undergroundInferred: z.boolean().optional(),
  spurConnector: z.boolean().optional(),
  realCorridor: z.boolean().optional(),
  corridorRef: z.string().nullish(),
  corridorSource: z.string().nullish(),
  corridorConfidence: z.string().nullish(),
  // V3-swap (build log 75, additive): cables (was silently stripped pre-.strict()), `rewired` (reduce.py
  // gen-rewire flag, validated-but-not-carried), and superior V3 edge attrs — voltage provenance + per-END
  // breaker state (uClosed/vClosed, the connections_map per-connection switch) + provenance label.
  cables: z.number().nullish(),
  rewired: z.boolean().optional(),
  voltageProvenance: z.string().nullish(),
  uClosed: z.boolean().optional(),
  vClosed: z.boolean().optional(),
  attrSource: z.string().nullish(),
  // V3-swap P6 + REV-A: a retired CORE transmission line superseded by the V3 backbone (isTie ⇒ engine opens
  // it; supersededLegacy ⇒ renderer hides it from the default map). Kept in data, never deleted.
  supersededLegacy: z.boolean().optional(),
}).strict(); // MF3 fail-loud: an unschematized field now THROWS at load (no silent drop = no silent downgrade)

export const SourceFuel = z.enum([
  // already domain-valid
  "NUCLEAR",
  "HYDRO",
  "GAS_CCGT",
  "GAS_CHP",
  "GAS",
  "WIND",
  "SOLAR",
  "BATTERY",
  "BIOGAS",
  "EFW",
  "OTHER",
  // source synonyms → normalized in load.ts
  "BIOGAS_LFG",
  "GAS_COGEN",
  "GAS_PEAKER",
  "GAS_OIL_STEAM",
  "BIOENERGY",
  "WASTE_TO_ENERGY",
]);

export const SourceGenerator = z.object({
  id: z.string(),
  busId: z.string(),
  name: z.string(),
  fuel: SourceFuel,
  capMw: z.number(),
  costPerMwh: z.number(),
  gridRole: z.enum(["gta_internal", "import_transmission", "external_embedded"]),
  mustRun: z.boolean().optional(),
  coord: Coord,
  // ── v3 restored Debug 3 dispatch-dynamics attributes (domain.v3.md) ──
  status: z.enum(["Operating", "Under_Construction", "Retired", "Decommissioned"]).optional(),
  minOutputMw: z.number().optional(), // NEGATIVE for BESS = charging capability
  rampRateMwPerMin: z.number().optional(),
  storageEnergyMwh: z.number().optional(),
  storageDurationH: z.number().optional(),
  chargeMw: z.number().optional(), // domain.v5 P4 (C34): explicit BATTERY C-rate power ratings
  dischargeMw: z.number().optional(),
  roundTripEfficiency: z.number().optional(),
  heatRateBtuPerKwh: z.number().optional(),
  co2KgPerMwh: z.number().optional(),
  capacityFactor: z.number().optional(),
  networkRole: z.string().optional(),
  meritRank: z.number().optional(),
  isChp: z.boolean().optional(),
  blackStartCapable: z.boolean().optional(),
  fuelSubtype: z.string().optional(),
  operator: z.string().optional(),
  iesoZone: z.string().optional(),
  // v3 Debug-3 availability (was emitted but silently stripped pre-.strict) — capMw derate (default 1).
  availabilityFactor: z.number().optional(),
  // P-CAL (build log 76): provenance for a curated nameplate correction (e.g. "electrical_standards.plant_overrides").
  attrSource: z.string().nullish(),
  // ── P11 (domain.v4): delivered-cost pricing — distance + transmission-loss-adjusted merit key + corridor ──
  distanceFromGtaKm: z.number().optional(),
  deliveredCostPerMwh: z.number().optional(),
  importCorridor: z.string().optional(),
}).strict(); // V3-swap P4 (MF3): fail loud on an unschematized generator field (no silent downgrade)

const Profile24 = z.array(z.number()).length(24);

export const SourceLoad = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(["consumer", "prosumer"]),
  busId: z.string(),
  maxDemandMw: z.number(),
  criticalityPriority: z.number(),
  developmentDensity: z.number(),
  loopType: z.string().nullish(),
  coord: Coord,
  areaPolygon: GeoArea.optional(),
  peakSolarMw: z.number().optional(),
  batteryCapacityMwh: z.number().optional(),
  batteryChemistry: z.string().optional(),
  // F4a (P11): offline-stamped distribution-loss coefficients (V3/v4/stamp-distloss.mjs) — additive optional.
  distLossCoef: z.object({ constPct: z.number(), quadCoef: z.number(), peakMw: z.number() }).optional(),
  // ── v3 restored Debug 3 per-neighbourhood demand model + prosumer economics (domain.v3.md) ──
  demandProfiles: z
    .object({
      summerWeekday: Profile24,
      summerWeekend: Profile24,
      winterWeekday: Profile24,
      winterWeekend: Profile24,
    })
    .optional(),
  peakHourSummer: z.number().int().min(0).max(23).optional(),
  peakHourWinter: z.number().int().min(0).max(23).optional(),
  seasonalScaling: z
    .object({ winter: z.number(), spring: z.number(), summer: z.number(), fall: z.number() })
    .optional(),
  weatherSensSummerMwPerDegC: z.number().optional(),
  weatherSensWinterMwPerDegC: z.number().optional(),
  annualConsumptionGwh: z.number().optional(),
  loadFactorPct: z.number().optional(),
  rooftopSuitabilityScore: z.number().optional(),
  batteryEtaAcAc: z.number().optional(),
  sellOfferPriceCkWh: z.number().optional(),
  population: z.number().optional(),
  populationDensityPerKm2: z.number().optional(),
  areaKm2: z.number().optional(),
});

// Extra report keys (generatedAt, source, loads, generators) are stripped by Zod's default behavior.
export const SourceReport = z.object({
  nodesBefore: z.number(),
  nodesAfter: z.number(),
  edgesBefore: z.number(),
  edgesAfter: z.number(),
  seriesEliminations: z.number(),
  stubPrunes: z.number(),
  prunedBranches: z.number(),
  removedByKind: z.record(z.string(), z.number()),
  reducibleDeg2Left: z.number(),
  deg1StubsLeft: z.number(),
});

export type SourceNodeT = z.infer<typeof SourceNode>;
export type SourceEdgeT = z.infer<typeof SourceEdge>;
export type SourceGeneratorT = z.infer<typeof SourceGenerator>;
export type SourceLoadT = z.infer<typeof SourceLoad>;
export type GeoAreaT = z.infer<typeof GeoArea>;
